import getCookieValue from './getCookieValue';
import { logger } from './logger';
import setCookieValues from './setCookieValue';

const FBCLID_COOKIE_KEY = 'fbclid';
const FBP_COOKIE_KEY = '_fbp';
const FBC_COOKIE_KEY = '_fbc';
const NINETY_DAYS = 90;

/**
 * Captures the fbclid parameter from URL and stores it in a cookie
 * This is used to track Facebook Click IDs for Meta Conversions API
 *
 * Protection against race conditions:
 * - Only processes fbclid once per session (checks existing cookie first)
 * - Preserves original timestamp from first click (important for Meta attribution)
 * - Prevents unnecessary cookie writes on component re-renders
 */
export const captureFbclid = (): void => {
  try {
    // Check if fbclid is already captured to prevent race conditions
    const existingFbclid = getFbclid();
    if (existingFbclid) {
      logger.debug('Facebook Click ID already captured', { fbclid : existingFbclid });
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get('fbclid');

    if (fbclid) {
      // Store fbclid in cookie for 90 days (Meta recommendation)
      setCookieValues([{
        key : FBCLID_COOKIE_KEY,
        value : fbclid
      }], true, NINETY_DAYS);

      // Also create _fbc cookie with fbclid
      // Format: fb.1.timestamp.fbclid
      // Timestamp represents the first click time (not subsequent re-renders)
      const timestamp = Date.now();
      const fbcValue = `fb.1.${timestamp}.${fbclid}`;

      setCookieValues([{
        key : FBC_COOKIE_KEY,
        value : fbcValue
      }], true, NINETY_DAYS);

      logger.info('Facebook Click ID captured', { fbclid, timestamp });
    }
  } catch (error) {
    logger.error('Error capturing fbclid', {
      error : error instanceof Error ? error.message : String(error)
    });
  }
};

/**
 * Gets the stored fbclid from cookie
 * @returns The fbclid value or null if not found
 */
export const getFbclid = (): string | null => {
  const fbclid = getCookieValue({ cookieKey : FBCLID_COOKIE_KEY as string, needPrefix : false });
  return fbclid || null;
};

/**
 * Gets the Facebook Browser ID (_fbp cookie)
 * This cookie is automatically set by Meta Pixel
 * @returns The _fbp value or null if not found
 */
export const getFbp = (): string | null => getCookieValue({ cookieKey : FBP_COOKIE_KEY as string, needPrefix : false });

/**
 * Gets the Facebook Click ID cookie (_fbc)
 * Format: fb.1.timestamp.fbclid
 * @returns The _fbc value or null if not found
 */
export const getFbc = (): string | null => getCookieValue({ cookieKey : FBC_COOKIE_KEY as string, needPrefix : false });

/**
 * Gets all Facebook tracking parameters for Meta Conversions API
 * @returns Object with fbp, fbc, and fbclid values
 */
export const getFacebookTrackingParams = () => {
  return {
    fbp : getFbp(),
    fbc : getFbc(),
    fbclid : getFbclid()
  };
};
